import React, { useState, useEffect } from 'react';
import { 
  Globe, Save, Plus, Trash2, X, Check, AlertCircle,
  Edit2, FileText
} from 'lucide-react';

function SearchPromptsConfig({ onClose }) {
  const [prompts, setPrompts] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editingCountry, setEditingCountry] = useState(null);
  const [newCountry, setNewCountry] = useState('');
  const [newPrompt, setNewPrompt] = useState('');
  const [showAddCountry, setShowAddCountry] = useState(false);

  useEffect(() => {
    loadPrompts();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const API_URL = window.API_CONFIG?.API_URL || process.env.REACT_APP_API_URL || 
    (window.location.hostname === 'localhost' 
      ? 'http://localhost:12343' 
      : `${window.location.protocol}//${window.location.hostname.replace('era-classifier', 'era-classifier-api')}`);
  const API_KEY = process.env.REACT_APP_API_KEY || '';

  const loadPrompts = async () => {
    try {
      const response = await fetch(`${API_URL}/api/v1/search-prompts`, {
        headers: {
          'X-API-Key': API_KEY
        }
      });
      const data = await response.json();
      setPrompts(data);
      setLoading(false);
    } catch (err) {
      setError('Failed to load search prompts');
      setLoading(false);
    }
  };

  const savePrompts = async () => {
    setSaving(true);
    setError('');
    setSuccess('');
    
    try {
      const response = await fetch(`${API_URL}/api/v1/search-prompts`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': API_KEY
        },
        body: JSON.stringify(prompts),
      });
      
      if (response.ok) {
        setSuccess('Search prompts saved successfully!');
        setTimeout(() => setSuccess(''), 3000);
      } else {
        throw new Error('Failed to save prompts');
      }
    } catch (err) {
      setError('Failed to save prompts');
    } finally {
      setSaving(false);
    }
  };

  const updateDefaultPrompt = (value) => {
    setPrompts({
      ...prompts,
      default_prompt: value
    });
  };

  const updateCountryPrompt = (country, value) => {
    setPrompts({
      ...prompts,
      country_prompts: {
        ...prompts.country_prompts,
        [country]: value
      }
    });
  };

  const deleteCountryPrompt = (country) => {
    const newCountryPrompts = { ...prompts.country_prompts };
    delete newCountryPrompts[country];
    setPrompts({
      ...prompts,
      country_prompts: newCountryPrompts
    });
  };

  const addCountryPrompt = () => {
    if (!newCountry || !newPrompt) return;
    
    setPrompts({
      ...prompts,
      country_prompts: {
        ...prompts.country_prompts,
        [newCountry]: newPrompt
      }
    });
    
    setNewCountry('');
    setNewPrompt('');
    setShowAddCountry(false);
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-xl p-8">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        <div className="p-6 border-b bg-gradient-to-r from-green-600 to-green-700 text-white">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold flex items-center">
              <Globe className="mr-3" />
              Search Prompts Configuration
            </h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors"
            >
              <X size={24} />
            </button>
          </div>
        </div>

        <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center text-red-700">
              <AlertCircle className="mr-2" size={20} />
              {error}
            </div>
          )}

          {success && (
            <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center text-green-700">
              <Check className="mr-2" size={20} />
              {success}
            </div>
          )}

          <div className="space-y-6">
            {/* Default Prompt */}
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                <FileText className="mr-2" size={20} />
                Default Prompt
              </h3>
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-sm text-gray-600 mb-2">
                  This prompt is used for countries without specific configurations
                </p>
                <textarea
                  value={prompts.default_prompt}
                  onChange={(e) => updateDefaultPrompt(e.target.value)}
                  className="w-full px-4 py-3 border rounded-lg resize-none"
                  rows="3"
                  placeholder="Enter default prompt..."
                />
                <p className="text-xs text-gray-500 mt-2">
                  Available variables: {'{company_name}'}, {'{country}'}
                </p>
              </div>
            </div>

            {/* Country-Specific Prompts */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-800 flex items-center">
                  <Globe className="mr-2" size={20} />
                  Country-Specific Prompts
                </h3>
                <button
                  onClick={() => setShowAddCountry(true)}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center"
                >
                  <Plus className="mr-2" size={18} />
                  Add Country
                </button>
              </div>

              <div className="space-y-4">
                {Object.entries(prompts.country_prompts || {}).map(([country, prompt]) => (
                  <div key={country} className="border rounded-lg p-4 bg-gray-50">
                    <div className="flex items-start justify-between mb-2">
                      <h4 className="font-medium text-gray-800 flex items-center">
                        <Globe className="mr-2" size={16} />
                        {country}
                      </h4>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setEditingCountry(editingCountry === country ? null : country)}
                          className="p-2 text-blue-600 hover:bg-blue-100 rounded-lg"
                        >
                          <Edit2 size={16} />
                        </button>
                        <button
                          onClick={() => deleteCountryPrompt(country)}
                          className="p-2 text-red-600 hover:bg-red-100 rounded-lg"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                    
                    {editingCountry === country ? (
                      <textarea
                        value={prompt}
                        onChange={(e) => updateCountryPrompt(country, e.target.value)}
                        className="w-full px-3 py-2 border rounded-lg resize-none"
                        rows="3"
                      />
                    ) : (
                      <p className="text-sm text-gray-600">{prompt}</p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="p-6 border-t bg-gray-50 flex justify-end space-x-4">
          <button
            onClick={onClose}
            className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-100"
          >
            Cancel
          </button>
          <button
            onClick={savePrompts}
            disabled={saving}
            className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center disabled:opacity-50"
          >
            <Save className="mr-2" size={18} />
            {saving ? 'Saving...' : 'Save Prompts'}
          </button>
        </div>
      </div>

      {/* Add Country Modal */}
      {showAddCountry && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-60">
          <div className="bg-white rounded-xl p-6 max-w-lg w-full">
            <h3 className="text-lg font-semibold mb-4">Add Country-Specific Prompt</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Country Name
                </label>
                <input
                  type="text"
                  value={newCountry}
                  onChange={(e) => setNewCountry(e.target.value)}
                  className="w-full px-4 py-2 border rounded-lg"
                  placeholder="e.g., Spain, Italy, Japan..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Search Prompt
                </label>
                <textarea
                  value={newPrompt}
                  onChange={(e) => setNewPrompt(e.target.value)}
                  className="w-full px-4 py-2 border rounded-lg resize-none"
                  rows="3"
                  placeholder="Generate a brief summary for {company_name} a company based in {country}..."
                />
                <p className="text-xs text-gray-500 mt-1">
                  Available variables: {'{company_name}'}, {'{country}'}
                </p>
              </div>
            </div>
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => {
                  setShowAddCountry(false);
                  setNewCountry('');
                  setNewPrompt('');
                }}
                className="px-4 py-2 border rounded-lg hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                onClick={addCountryPrompt}
                disabled={!newCountry || !newPrompt}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
              >
                Add Country
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SearchPromptsConfig;